import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import styled from 'styled-components';
import {
	Button,
	DropdownSelect,
	EmptyTableBody,
	LoadingPage,
	palette,
	PaginationFooter,
	rem,
	Text,
	error,
	success,
	StylovyzeTooltip,
} from '@innovyze/stylovyze';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Grid, Link, Stack } from '@mui/material';

import { selectDeteriorationModelsCards } from '@Selectors';
import { listDeteriorationModels, updatePublishedStatus } from '@Actions';
import { useGlobalization } from '@Translations';
import { addRouteNamespace } from '@Utils/actions';
import {
	newDeteriorationModel,
	copyDeteriorationModel,
	removeDeteriorationModel,
} from '@Services/deteriorationModels.services';

import { CreateDeteriorationModelDialog } from './CreateDeteriorationModelDialog.component';
import { DeleteDeteriorationModelDialog } from './DeleteDeteriorationModelDialog';
import { DeteriorationModelCard } from './DeteriorationModelCard.component';
import { getApiErrorMessage, isDuplicateNameError } from '@Utils/apiError';

const Bar = styled.div`
	width: 100%;
	background-color: #dcdcdc;
	padding: 10px;
`;

export const DeteriorationModelsList = () => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const history = useHistory();

	const [openModelDeteriorationDialog, setOpenModelDeteriorationDialog] = useState(false);
	const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; modelId: string; modelName: string }>({
		open: false,
		modelId: '',
		modelName: '',
	});
	const [isDeleting, setIsDeleting] = useState(false);

	const [deteriorationModelsListRequest, setDeteriorationModelsListRequest] = useState({
		page: 1,
		slice: 10,
		sortKey: 'updated_at',
		sortDescending: true,
	});

	const deteriorationModelsCards = selectDeteriorationModelsCards();
	useEffect(() => {
		dispatch(listDeteriorationModels(deteriorationModelsListRequest));
	}, [deteriorationModelsListRequest]);

	const refreshList = () => {
		dispatch(listDeteriorationModels(deteriorationModelsListRequest));
	};

	const handleCreate = async (request: {
		modelName: string;
		assetGroupSet: string;
		failureDefinition: string | null;
	}) => {
		try {
			const response = await newDeteriorationModel({
				modelName: request.modelName,
				assetGroupSet: request.assetGroupSet,
				failureDefinition: request.failureDefinition,
			});

			setOpenModelDeteriorationDialog(false);
			history.push(addRouteNamespace(`models/${response.data.model_id}`));
		} catch (e) {
			console.error('Error creating deterioration model:', e);
			if (isDuplicateNameError(e)) {
				const duplicateError = new Error(getApiErrorMessage(e, t('Enter a unique name')));
				(duplicateError as Error & { isDuplicateName: boolean }).isDuplicateName = true;
				throw duplicateError;
			}
			dispatch(error(t('Unable to create deterioration model')));
			throw e;
		}
	};

	const handleDuplicate = async (modelId: string) => {
		try {
			const response = await copyDeteriorationModel(modelId);

			dispatch(success(t('Deterioration model duplicated successfully')));
			refreshList();
			history.push(addRouteNamespace(`models/${response.data.model_id}`));
		} catch (e) {
			console.error('Error duplicate deterioration model:', e);
			dispatch(error(t('Unable to duplicate deterioration model')));
		}
	};

	const handleDelete = (modelId: string) => {
		const model = deteriorationModelsCards.cards.find(card => card.id === modelId);
		if (model) {
			setDeleteDialog({
				open: true,
				modelId: modelId,
				modelName: model.name,
			});
		}
	};

	const handleConfirmDelete = async () => {
		setIsDeleting(true);
		try {
			await removeDeteriorationModel(deleteDialog.modelId);
			dispatch(success(t('Deterioration model deleted successfully')));
			refreshList();
		} catch (e) {
			console.error('Error delete deterioration model:', e);
			dispatch(error(t('Unable to delete deterioration model')));
		} finally {
			setIsDeleting(false);
			setDeleteDialog({ open: false, modelId: '', modelName: '' });
		}
	};

	const handleCancelDelete = () => {
		setDeleteDialog({ open: false, modelId: '', modelName: '' });
	};

	const handlePublishToggle = (modelId: string, published: boolean) => {
		dispatch(
			updatePublishedStatus({
				modelId,
				published,
			}),
		);
		refreshList();
	};

	const emptyView = (
		<Stack sx={{ mb: 4 }}>
			<Bar>
				<Stack direction={'row'} spacing={1}>
					<Text variant="heading-medium-strong">{t('Deterioration Models')}</Text>
					<StylovyzeTooltip
						heading={t('Create Deterioration Model')}
						content={t(
							'Assign survival curves to your asset groups - from your own failure data or industry standards. ' +
								// eslint-disable-next-line prettier/prettier
									'You\'ll get failure probability and remaining useful life for each asset.',
						)}
						placement="right"
						padded
						componentsProps={{
							tooltip: {
								sx: {
									fontSize: '14px',
								},
							},
						}}>
						<InfoOutlinedIcon sx={{ fontSize: 'x-large', color: palette.secondary.main }} />
					</StylovyzeTooltip>
				</Stack>
			</Bar>
			<div
				style={{
					padding: '25px',
				}}>
				<EmptyTableBody
					icon={
						<div
							style={{
								height: '72px',
								width: '72px',
								paddingBottom: '10px',
							}}>
							<LeaderboardIcon
								style={{
									height: '72px',
									width: '72px',
								}}
							/>
						</div>
					}
					title={t('Create Deterioration Model')}
					subtitle={t(
						'Assign survival curves to your asset groups - from your own failure data or industry standards. ' +
							// eslint-disable-next-line prettier/prettier
							'You\'ll get failure probability and remaining useful life for each asset.',
					)}
					CTA={
						<Button
							variant="contained"
							color="primary"
							disabled={false}
							onClick={() => setOpenModelDeteriorationDialog(true)}>
							{t('Create Deterioration Model')}
						</Button>
					}
				/>
				<Stack alignItems={'center'}>
					<Link onClick={() => history.push('/agsets')} style={{ cursor: 'pointer' }}>
						{t('Go to Asset Group')}
					</Link>
				</Stack>
			</div>
		</Stack>
	);

	const viewWithCards = (
		<Stack>
			<Bar>
				<Stack direction="row" justifyContent="space-between">
					<Stack direction={'row'} spacing={1}>
						<Text variant="heading-medium-strong">{t('Deterioration Models')}</Text>
						<StylovyzeTooltip
							heading={t('Create Deterioration Model')}
							content={t(
								'Assign survival curves to your asset groups - from your own failure data or industry standards. ' +
									// eslint-disable-next-line prettier/prettier
									'You\'ll get failure probability and remaining useful life for each asset.',
							)}
							placement="right"
							padded
							componentsProps={{
								tooltip: {
									sx: {
										fontSize: '14px',
									},
								},
							}}>
							<InfoOutlinedIcon sx={{ fontSize: 'x-large', color: palette.secondary.main }} />
						</StylovyzeTooltip>
					</Stack>
					<Button variant="contained" onClick={() => setOpenModelDeteriorationDialog(true)}>
						{t('Create Deterioration Model')}
					</Button>
				</Stack>
			</Bar>
			<div
				style={{
					padding: '25px',
				}}>
				{deteriorationModelsCards.isLoading ? (
					<LoadingPage />
				) : (
					<>
						<DropdownSelect
							currentSelection={`${deteriorationModelsListRequest.sortKey}#${
								deteriorationModelsListRequest.sortDescending ? 'desc' : 'asc'
							}`}
							title={t('Sort by')}
							options={[
								{
									value: t('Modified At - Ascending'),
									key: 'updated_at#asc',
								},
								{
									value: t('Modified At - Descending'),
									key: 'updated_at#desc',
								},
							]}
							onChange={e => {
								const [sortKey, sortOrder] = e.target.value.split('#');
								const sortDescending = sortOrder === 'desc';
								setDeteriorationModelsListRequest({
									...deteriorationModelsListRequest,
									sortKey: sortKey,
									sortDescending: sortDescending,
								});
							}}
						/>
						<Grid container spacing={2} direction="row" style={{ padding: `${rem(16)} ${rem(0)}` }}>
							{deteriorationModelsCards.cards.map(card => (
								<Grid xs={12} sm={6} md={4} lg={3} xl={3} item key={card.id}>
									<DeteriorationModelCard
										model={card}
										onDuplicate={handleDuplicate}
										onDelete={handleDelete}
										onPublishToggle={handlePublishToggle}
									/>
								</Grid>
							))}
						</Grid>
						<Stack direction="row" justifyContent="space-between">
							<DropdownSelect
								inlineStyle={{ minWidth: '10%' }}
								currentSelection={deteriorationModelsCards.pagination.slice.toString()}
								title={t('Models per page')}
								options={[
									{
										value: t('5'),
										key: '5',
									},
									{
										value: t('10'),
										key: '10',
									},
									{
										value: t('15'),
										key: '15',
									},
									{
										value: t('25'),
										key: '25',
									},
									{
										value: t('50'),
										key: '50',
									},
									{
										value: t('100'),
										key: '100',
									},
								]}
								onChange={e =>
									setDeteriorationModelsListRequest({
										...deteriorationModelsListRequest,
										slice: Number(e.target.value),
									})
								}
							/>
							<PaginationFooter
								paginationInfo={{
									total: deteriorationModelsCards.pagination.total,
									previous:
										deteriorationModelsCards.pagination.page > 1 ? deteriorationModelsCards.pagination.page - 1 : 1,
									next:
										deteriorationModelsCards.pagination.page < deteriorationModelsCards.pagination.pages
											? deteriorationModelsCards.pagination.page + 1
											: deteriorationModelsCards.pagination.pages,
									current: deteriorationModelsCards.pagination.page,
									pages: deteriorationModelsCards.pagination.pages,
								}}
								onChange={(_e, page: number) =>
									setDeteriorationModelsListRequest({
										...deteriorationModelsListRequest,
										page: page,
									})
								}
							/>
						</Stack>
					</>
				)}
			</div>
		</Stack>
	);

	return (
		<>
			<CreateDeteriorationModelDialog
				setOpen={setOpenModelDeteriorationDialog}
				isOpen={openModelDeteriorationDialog}
				onCreate={handleCreate}
			/>
			<DeleteDeteriorationModelDialog
				open={deleteDialog.open}
				modelName={deleteDialog.modelName}
				onClose={handleCancelDelete}
				onConfirm={handleConfirmDelete}
				isDeleting={isDeleting}
			/>
			{deteriorationModelsCards.isLoading || deteriorationModelsCards.cards.length > 0 ? viewWithCards : emptyView}
		</>
	);
};
